declare global {
	interface Window {
		testAnalytics?: any;
	}
}

import Cookie from 'js-cookie';

import { AllAnalyticsCookies } from './cookie';
import { TestSupport } from './util/testSupport';

const MAX_CACHE_SIZE = 100;
const COOKIE_NAME = AllAnalyticsCookies.AWC_ANALYTICS_DEBUG.getKey();
const DEBUG_TAG = 'synthetic';

class TestingCache {
	private readonly _isDebugCookieEnabled: boolean;
	private readonly _syntheticFlagOverride?: boolean;
	private readonly _enableTestingCache?: boolean;

	constructor(syntheticFlagOverride?: boolean | null, enableTestingCache?: boolean | null) {
		this._syntheticFlagOverride =
			typeof syntheticFlagOverride === 'boolean' ? syntheticFlagOverride : undefined;
		this._enableTestingCache =
			typeof enableTestingCache === 'boolean' ? enableTestingCache : undefined;
		this._isDebugCookieEnabled = TestingCache.getDebugCookie();
		this.bind();
	}

	public saveEvent(event?: any): void {
		try {
			if (!event) {
				return;
			}

			this.bind();
			this.addSyntheticTag(event);
			this.cache(event);
		} catch {
			// Ignore errors from testing cache.
		}
	}

	private bind(): void {
		if (!this.shouldCacheEvent() || typeof window === 'undefined' || window.testAnalytics) {
			return;
		}

		window.testAnalytics = {
			eventCache: [],
			events: () => window.testAnalytics.eventCache,
			clear: () => {
				window.testAnalytics.eventCache = [];
				try {
					localStorage.removeItem('awc.ui.viewed.last.sent');
					localStorage.removeItem('awc-staging.ui.viewed.last.sent');
					localStorage.removeItem('awc-dev.ui.viewed.last.sent');
					localStorage.removeItem('awc-local.ui.viewed.last.sent');
				} catch {
					// Ignore errors from localStorage.
				}
			},
		};
	}

	private cache(event: any): void {
		if (!this.shouldCacheEvent() || typeof window === 'undefined' || !window.testAnalytics) {
			return;
		}

		if (window.testAnalytics.eventCache.length >= MAX_CACHE_SIZE) {
			window.testAnalytics.eventCache = window.testAnalytics.eventCache.slice(
				1,
				window.testAnalytics.eventCache.length,
			);
		}
		window.testAnalytics.eventCache.push(event);
	}

	private addSyntheticTag(event: any): void {
		if (!this.shouldSetSyntheticFlag()) {
			return;
		}

		if (event.tags instanceof Array) {
			if (!event.tags.includes(DEBUG_TAG)) {
				event.tags.push(DEBUG_TAG);
			}
		} else {
			event.tags = [DEBUG_TAG];
		}
	}

	private shouldCacheEvent(): boolean {
		return this._enableTestingCache ? this._enableTestingCache : this._isDebugCookieEnabled;
	}

	private shouldSetSyntheticFlag(): boolean {
		if (this._syntheticFlagOverride !== undefined) {
			return this._syntheticFlagOverride;
		}

		const globalSyntheticOverride = TestSupport.getSyntheticFlagOverride();
		if (globalSyntheticOverride !== undefined) {
			return globalSyntheticOverride;
		}

		return this.shouldCacheEvent();
	}

	private static getDebugCookie(): boolean {
		try {
			return JSON.parse(Cookie.get(COOKIE_NAME) || 'false') === true;
		} catch {
			Cookie.remove(COOKIE_NAME);
		}
		return false;
	}
}

export default TestingCache;
